import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { isNativePlatform } from '../../utils/capacitorUtils';

// Import authentication components
import { trackSignup, trackLogin } from '../../utils/metaPixel';
import { useTheme } from '../../contexts/ThemeContext';
import MobileAppLogo from '../mobile/MobileAppLogo';
import MobileOnboarding, { hasCompletedOnboarding } from '../mobile/MobileOnboarding';

import OAuthLoginForm from './components/OAuthLoginForm';
import AuthPinCenteredLogin from './AuthPinCenteredLogin';
import AuthPinCenteredSignup from './AuthPinCenteredSignup';

// Import types
import type { AuthView, AuthProps } from './types';


const Auth: React.FC<AuthProps> = ({ 
  initialView = 'login', 
  onNavigate, 
  onAuthSuccess 
}) => {
  const { t: tFn, i18n } = useTranslation();
  const t = typeof tFn === 'function' ? tFn : ((key: string) => key);
  const { theme } = useTheme();
  // Use initialView to determine auth mode
  const [currentView] = useState<AuthView>(initialView);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<{ planId: string; billing: string } | null>(null);

  // Check for OAuth errors from sessionStorage on mount
  useEffect(() => {
    const oauthError = sessionStorage.getItem('oauth_error');
    if (oauthError) {
      setError(oauthError);
      sessionStorage.removeItem('oauth_error');
    }
  }, []);

  // Detect plan from URL params on mount (plan = pricing page id: basic | professional)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const planId = urlParams.get('plan');
    const billing = urlParams.get('billing');
    if (planId && billing) {
      setSelectedPlan({ planId, billing });
    }
  }, []);

  // Pin-style auth pages on web — respect light/dark theme
  useEffect(() => {
    if (isNativePlatform()) return undefined;
    document.documentElement.classList.add('auth-page-active');
    if (theme === 'light') {
      document.documentElement.classList.add('auth-page-light');
      document.documentElement.classList.remove('auth-page-dark');
    } else {
      document.documentElement.classList.add('auth-page-dark');
      document.documentElement.classList.remove('auth-page-light');
    }
    return () => {
      document.documentElement.classList.remove('auth-page-active', 'auth-page-light', 'auth-page-dark');
    };
  }, [theme]);

  // Map pricing page plan id to subscription_plans table id (createSubscription expects DB plan_id)
  const getSubscriptionPlanId = (urlPlanId: string): string => {
    const map: Record<string, string> = {
      basic: 'professional',       // Pricing "Basic" plan -> subscription_plans.professional
      professional: 'premium',      // Pricing "Professional" plan -> subscription_plans.premium
    };
    return map[urlPlanId] || urlPlanId;
  };

  // Handle successful authentication
  const handleAuthSuccess = async () => {
    setError(null);
    if (currentView === 'signup') trackSignup();
    else if (currentView === 'login') trackLogin();
    
    // If there's a selected plan, initiate payment
    if (selectedPlan) {
      try {
        setSuccess(t('auth.redirectingToPayment'));
        
        // Import the payment service
        const { createSubscription } = await import('../../services/subscriptionService.cashfree');
        const { supabase } = await import('../../lib/supabase');
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setError(t('auth.userNotFound'));
          return;
        }
        
        // Get user details
        const { data: userData } = await supabase
          .from('users')
          .select('email, phone')
          .eq('user_id', user.id)
          .single();
        
        // Create subscription (map URL plan id to DB plan_id)
        const subscriptionPlanId = getSubscriptionPlanId(selectedPlan.planId);
        const result = await createSubscription({
          plan_id: subscriptionPlanId as 'professional' | 'premium' | 'enterprise',
          billing_cycle: selectedPlan.billing as 'monthly' | 'annual',
          currency: 'INR',
          customer_email: user.email || userData?.email || '',
          customer_phone: userData?.phone || '9999999999'
        });
        
        if (result.success && result.short_url) {
          // Redirect to Cashfree payment page
          window.location.href = result.short_url;
        } else {
          setError(t('auth.failedSubscription'));
          // Still call parent callback to go to dashboard
          window.setTimeout(() => {
            onAuthSuccess?.();
          }, 2000);
        }
      } catch (error) {
        console.error('Error initiating payment:', error);
        setError(t('auth.errorRedirecting'));
        window.setTimeout(() => {
          onAuthSuccess?.();
        }, 2000);
      }
    } else {
      setRedirecting(true);
      onAuthSuccess?.();
    }
  };

  // Handle authentication error
  const handleAuthError = (errorMessage: string) => {
    setRedirecting(false);
    setError(errorMessage);
    setSuccess(null);
  };

  // Removed switchView - no longer needed

  // Handle back to home
  const handleBackToHome = () => {
    onNavigate?.('home');
  };

  // ── Native Android: onboarding + redesigned login ───────────────────────
  if (isNativePlatform()) {
    // Show onboarding carousel only on first launch
    const [showOnboarding, setShowOnboarding] = useState(() => !hasCompletedOnboarding());

    if (showOnboarding) {
      return <MobileOnboarding onDone={() => setShowOnboarding(false)} />;
    }

    return (
      <div
        key={i18n.language}
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          /* Two themes: light = purple gradient, dark = black gradient (--ggj-header-bg) */
          background: 'var(--ggj-header-bg, var(--brand-popup-header))',
          paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
          transition: 'background 0.3s',
        }}
      >
        {/* ── Top hero: logo + brand ── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 24px 32px',
            gap: 20,
          }}
        >
          {/* MobileAppLogo already renders the "GoGrabJob" name — no extra h1 needed */}
          <MobileAppLogo iconSize={88} showTagline={false} />
          <p
            style={{
              fontSize: 14,
              /* Hero is always dark/coloured, so text is always near-white */
              color: 'rgba(255,255,255,0.72)',
              margin: 0,
              lineHeight: 1.4,
              textAlign: 'center',
            }}
          >
            {t('auth.mobileTagline', 'AI-powered job search companion')}
          </p>
        </div>

        {/* ── Bottom card: sign-in options (layered card — light/dark mockup) ── */}
        <div
          style={{
            background: 'var(--ggj-mobile-content-card-bg, var(--vscode-bg-primary))',
            borderRadius: '24px 24px 0 0',
            borderTop: 'var(--ggj-mobile-content-card-border-top, 1px solid var(--vscode-border-divider))',
            padding: '32px 24px',
            paddingBottom: 'max(32px, env(safe-area-inset-bottom, 16px))',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            transition: 'background 0.3s',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 4 }}>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: 'var(--vscode-text-primary)',
                margin: 0,
                letterSpacing: '-0.02em',
              }}
            >
              {currentView === 'signup'
                ? t('auth.createAccount', 'Create Account')
                : t('auth.welcomeBack', 'Welcome Back')}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--vscode-text-tertiary)', margin: '6px 0 0' }}>
              {t('auth.continueWith')}
            </p>
          </div>

          {/* Error / success banners */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{
                  padding: '10px 14px', borderRadius: 12,
                  background: 'var(--vscode-error-bg)', border: '1px solid var(--vscode-error-border)',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}
              >
                <AlertTriangle size={15} style={{ color: 'var(--vscode-error)', flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 13, color: 'var(--vscode-error)', margin: 0 }}>{error}</p>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{
                  padding: '10px 14px', borderRadius: 12,
                  background: 'var(--vscode-success-bg)', border: '1px solid var(--vscode-success-border)',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}
              >
                <CheckCircle size={15} style={{ color: 'var(--vscode-success)', flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 13, color: 'var(--vscode-success)', margin: 0 }}>{success}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* OAuth buttons */}
          <OAuthLoginForm
            authMode={currentView === 'signup' ? 'signup' : 'login'}
            onSuccess={handleAuthSuccess}
            onError={handleAuthError}
          />

          {/* Sign up / Login switch */}
          <div style={{ textAlign: 'center', paddingTop: 4 }}>
            {currentView === 'signup' ? (
              <p style={{ fontSize: 13, color: 'var(--vscode-text-tertiary)' }}>
                {t('auth.alreadyHaveAccount')}{' '}
                <button type="button" onClick={() => { window.location.href = '/login'; }}
                  style={{ color: 'var(--vscode-primary)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                  {t('auth.logIn')}
                </button>
              </p>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--vscode-text-tertiary)' }}>
                {t('auth.noAccount')}{' '}
                <button type="button" onClick={() => { window.location.href = '/signup'; }}
                  style={{ color: 'var(--vscode-primary)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                  {t('auth.signUp')}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  // ── END Native branch ─────────────────────────────────────────────────────

  const isSignup = currentView === 'signup';

  if (!isSignup) {
    return (
      <AuthPinCenteredLogin
        key={i18n.language}
        t={t}
        error={error}
        success={success}
        redirecting={redirecting}
        selectedPlan={selectedPlan}
        onBackToHome={handleBackToHome}
        onAuthStarted={() => setRedirecting(true)}
        onAuthSuccess={handleAuthSuccess}
        onAuthError={handleAuthError}
      />
    );
  }

  return (
    <AuthPinCenteredSignup
      key={i18n.language}
      t={t}
      error={error}
      success={success}
      redirecting={redirecting}
      selectedPlan={selectedPlan}
      onBackToHome={handleBackToHome}
      onAuthStarted={() => setRedirecting(true)}
      onAuthSuccess={handleAuthSuccess}
      onAuthError={handleAuthError}
    />
  );
};

export default Auth;
